import React, { useEffect, useState } from 'react';

import clsx from 'clsx';
import IconArrowDown from '@/assets/icons/icon-arrow-down-01.svg?react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import IconDocumentCode from '@/assets/icons/icon-document-code.svg?react';
import { Textarea } from '@/components/ui/textarea';
import { TextField } from '@/components/inspector-fields/TextField/TextField';
import { selectScriptsSettings } from '@/store/settings/scripts/selectors';
import { selectCurrentPageId } from '@/store/pages/selectors';
import { useAppSelector } from '@/store/hooks';
import { useSettingsDebounceUpdate } from '@/hooks/useSettingsDebounceUpdate';
import { shallowEqual } from '@/utils/settingsHelpers';
import type { ScriptsSettings } from '@builder4/shared/types/settings';

interface SettingsScriptsProps {
  className: string;
  onClick?: () => void;
  isOpenCollapse: boolean;
}

const defaultSettings: ScriptsSettings = {
  googleAnalyticsID: '',
  head: '',
  body: '',
  footer: '',
};

export const SettingsScripts: React.FC<SettingsScriptsProps> = ({
  className = '',
  onClick,
  isOpenCollapse,
}) => {
  const scriptsSettingsRedux = useAppSelector(selectScriptsSettings) ?? defaultSettings;
  const currentPageId = useAppSelector(selectCurrentPageId);
  const [isOpen] = useState(isOpenCollapse);
  const [scriptsSettings, setScriptsSettings] = useState<ScriptsSettings>(scriptsSettingsRedux);

  const handleClick = () => {
    if (onClick) onClick();
  };

  const handleUpdateScriptsSettings = <K extends keyof ScriptsSettings>(
    key: K,
    value: ScriptsSettings[K],
  ) => {
    setScriptsSettings((prevState) => ({ ...prevState, [key]: value }));
  };

  const { lastSavedValueRef } = useSettingsDebounceUpdate({
    value: scriptsSettings,
    bodyKey: 'scripts_settings',
    currentPageId: currentPageId,
    reduxValue: scriptsSettingsRedux,
  });

  useEffect(() => {
    if (!scriptsSettingsRedux) return;

    setScriptsSettings((prev) => {
      if (shallowEqual(prev, scriptsSettingsRedux)) return prev;
      if (
        lastSavedValueRef.current &&
        shallowEqual(scriptsSettingsRedux, lastSavedValueRef.current)
      ) {
        return prev;
      }
      return scriptsSettingsRedux;
    });
  }, [scriptsSettingsRedux, lastSavedValueRef]);

  return (
    <Collapsible defaultOpen={isOpen}>
      <CollapsibleTrigger
        onClick={handleClick}
        data-qa-selector="settings-scripts-toggle"
        className={clsx(
          'group bg-neutral-0 w-full cursor-pointer border-t border-neutral-100 px-2 py-4',
          className,
        )}
      >
        <div className="flex w-full items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <IconDocumentCode className="h-4 w-4 text-neutral-900" />
            <span className="text-xs text-neutral-900">Scripts</span>
          </div>
          <IconArrowDown className="h-4 w-4 text-neutral-500 transition-all group-aria-expanded:rotate-180" />
        </div>
      </CollapsibleTrigger>

      <CollapsibleContent className={clsx('bg-neutral-0 max-w-[232px] pb-3')}>
        <div className="flex flex-col gap-2">
          <div className="flex flex-col gap-2 p-2">
            <div className="my-3 text-[10px] font-medium text-neutral-400">
              <p>
                This section enables you to gather data by pasting tracking codes from the analytics
                service providers of your choice (e.g. Google Analytics, Facebook Pixel)
              </p>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1">
                <p className="text-button-sm text-neutral-500">Google Analytics ID</p>
              </div>
            </div>
            <TextField
              className="w-full max-w-[216px]"
              classNameInputParent="w-full max-w-full"
              inputValue={scriptsSettings.googleAnalyticsID ?? ''}
              onChange={(e) => handleUpdateScriptsSettings('googleAnalyticsID', e.target.value)}
              placeholderText="Ex: G-12345678"
              text=""
              type="text"
            />
            <p className="text-[10px] font-medium text-neutral-400">
              This Google Analytics 4 Measurement ID for your tracking code.
            </p>
          </div>

          <div className="flex flex-col gap-2 p-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1">
                <p className="text-button-sm text-neutral-500">Head Section Tracking Code</p>
              </div>
            </div>
            <Textarea
              className={'min-h-16 w-full resize-none'}
              placeholder={'Paste your code here...'}
              disabled={false}
              value={scriptsSettings.head ?? ''}
              onChange={(e) => handleUpdateScriptsSettings('head', e.target.value)}
            />
          </div>

          <div className="flex flex-col gap-2 p-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1">
                <p className="text-button-sm text-neutral-500">
                  Right after the opening &lt;body&gt; tag
                </p>
              </div>
            </div>
            <Textarea
              className={'min-h-16 w-full resize-none'}
              placeholder={'Paste your code here...'}
              disabled={false}
              value={scriptsSettings.body ?? ''}
              onChange={(e) => handleUpdateScriptsSettings('body', e.target.value)}
            />
          </div>

          <div className="flex flex-col gap-2 p-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1">
                <p className="text-button-sm text-neutral-500">
                  Right before the closing &lt;/body&gt; tag
                </p>
              </div>
            </div>
            <Textarea
              className={'min-h-16 w-full resize-none'}
              placeholder={'Paste your code here...'}
              disabled={false}
              value={scriptsSettings.footer ?? ''}
              onChange={(e) => handleUpdateScriptsSettings('footer', e.target.value)}
            />
          </div>

          <div className="my-3 flex flex-col items-start p-2 text-[10px] font-medium text-neutral-400">
            <p>
              Please note that we do not provide support for this section. Any non-tracking code
              placed in the above areas may cause major problems with your Page. For
              troubleshooting, please use the support and documentation provided by your analytics
              service provider.
            </p>
            <p className="pt-4">
              Want to learn more about these fields? <br />
              <a
                href="https://support.leadpages.com/hc/en-us/articles/115007582368-Third-party-tracking-and-analytics"
                className="text-indigo-500 underline"
                target="_blank"
                rel="noopener noreferrer"
              >
                Click here
              </a>
            </p>
          </div>
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};
